import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import {
  buildConditionalAccessBaselineRows,
  getConditionalAccessPolicyName,
  getConditionalAccessPolicyStatePresentation,
} from '../../data/conditionalAccessBaseline'
import { EMPTY_VALUE, TONE } from '../../data/constants'
import Chip from '../../components/Chip/Chip'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'
import styles from './IdentityAccessSection.module.scss'

const EMPTY_CELL = '-'

const getEnabledPolicyStatsTone = ({ enabled, total }) => {
  if (total === 0) return TONE.NEUTRAL
  if (enabled === total) return TONE.OK
  return TONE.WARN
}

export default function ConditionalAccess({ score, gap = null, summary, baselines = [] }) {
  const rows = buildConditionalAccessBaselineRows(baselines)
  const policyStats = {
    total: summary.conditionalAccess?.total ?? 0,
    enabled: summary.conditionalAccess?.enabled ?? 0,
    reportOnly: summary.conditionalAccess?.reportOnly ?? 0,
    enabledPct: summary.conditionalAccess?.enabledPct ?? 0,
  }
  const isUnavailable = Boolean(gap)
  let reportOnlyTone = TONE.NEUTRAL
  if (!isUnavailable) reportOnlyTone = policyStats.reportOnly > 0 ? TONE.WARN : TONE.OK

  const stats = [
    {
      key: 'enabled',
      value: isUnavailable ? EMPTY_VALUE : policyStats.enabled,
      denom: isUnavailable ? undefined : policyStats.total,
      tone: isUnavailable ? TONE.NEUTRAL : getEnabledPolicyStatsTone(policyStats),
      label: isUnavailable ? (
        <FormattedMessage id="AI_RISK_REPORT.CONDITIONAL_ACCESS.ENABLED_NOTE_UNAVAILABLE" />
      ) : (
        <FormattedMessage
          id="AI_RISK_REPORT.CONDITIONAL_ACCESS.ENABLED_NOTE"
          values={{ pct: policyStats.enabledPct }}
        />
      ),
    },
    {
      key: 'reportOnly',
      value: isUnavailable ? EMPTY_VALUE : policyStats.reportOnly,
      tone: reportOnlyTone,
      label: <FormattedMessage id="AI_RISK_REPORT.CONDITIONAL_ACCESS.REPORT_ONLY_NOTE" />,
    },
    {
      key: 'total',
      value: isUnavailable ? EMPTY_VALUE : policyStats.total,
      label: <FormattedMessage id="AI_RISK_REPORT.CONDITIONAL_ACCESS.TOTAL_POLICIES" />,
    },
  ]

  const columns = [
    {
      key: 'pattern',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_PATTERN" />,
      render: (row) => (
        <span className="text-body fw-bold">
          <FormattedMessage id={row.labelId} />
        </span>
      ),
      sortable: true,
      sortValue: (row) => row.labelId,
    },
    {
      key: 'policy',
      header: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.BASELINE.COL_MATCHED_POLICY" />,
      render: (row) =>
        row.matchedPolicy ? (
          <span>{getConditionalAccessPolicyName(row.matchedPolicy)}</span>
        ) : (
          <span className="text-muted">
            <FormattedMessage id="AI_RISK_REPORT.IDENTITY.CA.BASELINE.NO_MATCH" />
          </span>
        ),
      sortable: true,
      sortValue: (row) => getConditionalAccessPolicyName(row.matchedPolicy),
    },
    {
      key: 'coverage',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_COVERAGE" />,
      render: (row) => {
        if (!row.matchedPolicy) return EMPTY_CELL

        return (
          <div className="d-grid gap-2">
            <div className={styles.coverageRequirementsCount}>
              <FormattedMessage
                id="AI_RISK_REPORT.IDENTITY.CA.REQ.PASS_COUNT"
                values={{ passed: row.passed, total: row.total }}
              />
            </div>
            <div className="d-flex flex-wrap gap-2">
              {row.requirements.map((requirement) => (
                <Chip
                  key={requirement.key}
                  tone={requirement.met ? TONE.OK : TONE.DANGER}
                  className="d-inline-flex align-items-center gap-1"
                >
                  <i
                    className={`fas fa-${requirement.met ? 'check' : 'xmark'} ${styles.requirementIcon}`}
                    aria-hidden="true"
                  />
                  <FormattedMessage id={requirement.labelId} />
                </Chip>
              ))}
            </div>
          </div>
        )
      },
    },
    {
      key: 'status',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_STATUS" />,
      className: styles.statusColumn,
      render: (row) => {
        if (!row.matchedPolicy) return EMPTY_CELL
        return <FormattedMessage id={getConditionalAccessPolicyStatePresentation(row.matchedPolicy).messageId} />
      },
      sortable: true,
      sortValue: (row) => {
        if (!row.matchedPolicy) return 'zzz'
        return getConditionalAccessPolicyStatePresentation(row.matchedPolicy).sortValue
      },
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.IDENTITY.CA.TITLE"
      subtitleId="AI_RISK_REPORT.IDENTITY.CA.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={stats}
    >
      <ReportTable columns={columns} rows={rows} />
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  conditionalAccess: PropTypes.shape({
    total: PropTypes.number,
    enabled: PropTypes.number,
    reportOnly: PropTypes.number,
    enabledPct: PropTypes.number,
  }),
})

ConditionalAccess.propTypes = {
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string }),
  summary: summaryPropType.isRequired,
  baselines: PropTypes.arrayOf(PropTypes.shape({})),
}
